"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"

interface XPWeights {
  touchpoints: {
    outbound_message: number
    warm_follow_up: number
    response_received: number
    daily_cap_per_contact: number
  }
  pipeline_progress: {
    forward_stage_move: number
    submitted_to_client: number
    interviewing_scheduled: number
    hired: number
    daily_cap_per_candidate: number
  }
  tasks: {
    completed_on_time: number
    completed_late: number
    zero_overdues_weekly_bonus: number
  }
  pipeline_enrichment: {
    candidate_comment: number
    comment_daily_cap_per_candidate: number
    contact_fetched: number
  }
  weekly_targets: {
    xp_per_recruiter: number
    xp_upper_bound: number
  }
}

interface GameModeContextType {
  organizationGameMode: boolean
  setOrganizationGameMode: (enabled: boolean) => void
  teamMemberGameMode: boolean
  setTeamMemberGameMode: (enabled: boolean) => void
  gameModeEnabled: boolean
  xpWeights: XPWeights
  setXPWeights: (weights: XPWeights) => void
  resetXPWeights: () => void
}

const defaultXPWeights: XPWeights = {
  touchpoints: {
    outbound_message: 2,
    warm_follow_up: 3,
    response_received: 5,
    daily_cap_per_contact: 1,
  },
  pipeline_progress: {
    forward_stage_move: 8,
    submitted_to_client: 12,
    interviewing_scheduled: 15,
    hired: 100,
    daily_cap_per_candidate: 1,
  },
  tasks: {
    completed_on_time: 4,
    completed_late: 1,
    zero_overdues_weekly_bonus: 25,
  },
  pipeline_enrichment: {
    candidate_comment: 2,
    comment_daily_cap_per_candidate: 1,
    contact_fetched: 1,
  },
  weekly_targets: {
    xp_per_recruiter: 250,
    xp_upper_bound: 300,
  },
}

const GameModeContext = createContext<GameModeContextType | undefined>(undefined)

export function GameModeProvider({ children }: { children: ReactNode }) {
  const [organizationGameMode, setOrganizationGameMode] = useState(true)
  const [teamMemberGameMode, setTeamMemberGameMode] = useState(true)
  const [xpWeights, setXPWeights] = useState<XPWeights>(defaultXPWeights)

  const gameModeEnabled = organizationGameMode && teamMemberGameMode

  useEffect(() => {
    const savedOrgGameMode = localStorage.getItem("organizationGameMode")
    const savedTeamGameMode = localStorage.getItem("teamMemberGameMode")
    const savedXPWeights = localStorage.getItem("xpWeights")

    if (savedOrgGameMode !== null) {
      setOrganizationGameMode(JSON.parse(savedOrgGameMode))
    }

    if (savedTeamGameMode !== null) {
      setTeamMemberGameMode(JSON.parse(savedTeamGameMode))
    }

    if (savedXPWeights) {
      try {
        setXPWeights(JSON.parse(savedXPWeights))
      } catch (error) {
        console.error("Failed to parse saved XP weights:", error)
      }
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("organizationGameMode", JSON.stringify(organizationGameMode))
  }, [organizationGameMode])

  useEffect(() => {
    localStorage.setItem("teamMemberGameMode", JSON.stringify(teamMemberGameMode))
  }, [teamMemberGameMode])

  useEffect(() => {
    localStorage.setItem("xpWeights", JSON.stringify(xpWeights))
  }, [xpWeights])

  const resetXPWeights = () => {
    setXPWeights(defaultXPWeights)
  }

  return (
    <GameModeContext.Provider
      value={{
        organizationGameMode,
        setOrganizationGameMode,
        teamMemberGameMode,
        setTeamMemberGameMode,
        gameModeEnabled,
        xpWeights,
        setXPWeights,
        resetXPWeights,
      }}
    >
      {children}
    </GameModeContext.Provider>
  )
}

export function useGameMode() {
  const context = useContext(GameModeContext)
  if (context === undefined) {
    throw new Error("useGameMode must be used within a GameModeProvider")
  }
  return context
}
